import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ExperiencesAPI from "../services/ExperiencesAPI";
import { setViewerSession } from "../services/authToken";
import { getJoinFailureMessage } from "../utils/customerMessages";
import LanguagePicker from "../components/LanguagePicker";
import { useLocalization } from "../localization/LocalizationContext";

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const normalizeJoinCode = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");

const JoinPage = () => {
  const { dir, t } = useLocalization();
  const navigate = useNavigate();
  const { code: routeCode } = useParams();
  const autoRedeemedCodeRef = useRef(null);
  const normalizedRouteCode = useMemo(
    () => normalizeJoinCode(routeCode),
    [routeCode],
  );

  const [code, setCode] = useState(normalizedRouteCode);
  const [isSubmitting, setIsSubmitting] = useState(Boolean(normalizedRouteCode));
  const [errorMessage, setErrorMessage] = useState("");

  const redeemCode = useCallback(
    async (rawCode) => {
      const normalizedCode = normalizeJoinCode(rawCode);

      if (!normalizedCode) {
        setErrorMessage(t("join.invalid"));
        return;
      }

      setIsSubmitting(true);
      setErrorMessage("");

      try {
        const response = await ExperiencesAPI.post(
          "join-codes/redeem",
          { code: normalizedCode },
          { authMode: "none" },
        );
        const payload = getApiResponseData(response);

        if (!payload?.viewerAccessToken || !payload?.experienceId) {
          throw new Error("Join code redemption did not return a viewer session.");
        }

        setViewerSession({
          viewerAccessToken: payload.viewerAccessToken,
          experienceId: payload.experienceId,
          userData: payload.userData,
        });

        navigate(`/experiences/${encodeURIComponent(payload.experienceId)}`, {
          replace: true,
        });
      } catch (error) {
        setErrorMessage(getJoinFailureMessage(error, t));
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, t],
  );

  useEffect(() => {
    setCode(normalizedRouteCode);

    if (!normalizedRouteCode) {
      autoRedeemedCodeRef.current = null;
      setIsSubmitting(false);
      return;
    }

    if (autoRedeemedCodeRef.current === normalizedRouteCode) {
      return;
    }

    autoRedeemedCodeRef.current = normalizedRouteCode;
    redeemCode(normalizedRouteCode);
  }, [normalizedRouteCode, redeemCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    await redeemCode(code);
  };

  return (
    <Box
      dir={dir}
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background:
          "radial-gradient(circle at top left, rgba(39, 94, 107, 0.24) 0%, rgba(9, 15, 23, 0) 34%), linear-gradient(180deg, #060b10 0%, #0c141d 100%)",
      }}
    >
      <LanguagePicker
        sx={{
          position: "fixed",
          top: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          zIndex: 1400,
        }}
      />
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "linear-gradient(180deg, rgba(15, 23, 34, 0.96) 0%, rgba(10, 16, 24, 0.98) 100%)",
          boxShadow: "0 32px 80px rgba(0, 0, 0, 0.35)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="overline"
                sx={{ color: "rgba(255,255,255,0.64)", letterSpacing: "0.14em" }}
              >
                {t("join.eyebrow")}
              </Typography>
              <Typography variant="h4" sx={{ color: "#fff", mt: 0.5 }}>
                {t("join.title")}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", mt: 1 }}>
                {t("join.instructions")}
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label={t("join.code_label")}
                  value={code}
                  onChange={(event) =>
                    setCode(normalizeJoinCode(event.target.value))
                  }
                  placeholder="ABC1234"
                  autoFocus={!normalizedRouteCode}
                  fullWidth
                  inputProps={{
                    autoCapitalize: "characters",
                    autoCorrect: "off",
                    spellCheck: false,
                    maxLength: 16,
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      color: "#fff",
                      direction: "ltr",
                      letterSpacing: "0.18em",
                      fontSize: "1.05rem",
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.64)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.16)",
                    },
                  }}
                />

                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || !code}
                  sx={{ minHeight: 52 }}
                >
                  {isSubmitting ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} color="inherit" />
                      <span className="control-label">{t("join.loading")}</span>
                    </Stack>
                  ) : (
                    <span className="control-label">{t("join.action")}</span>
                  )}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default JoinPage;
